import type {
  FontFace,
  FontFamily,
  FontFormat,
  FontScope,
  FontStyle,
  ProjectFontRoles,
} from "../api/StylesAPI.types";
import type {
  AppearancePreviewState,
  SiteArchitectureFontFamily,
} from "../types/appearancePreview.types";

/** Le mot-clé `format()` du CSS ne reprend pas l'extension du fichier. */
const CSS_FORMAT_BY_FONT_FORMAT: Record<FontFormat, string> = {
  woff2: "woff2",
  woff: "woff",
  ttf: "truetype",
  otf: "opentype",
};

export const FONT_SCOPE_ORDER_LIST: FontScope[] = [
  "system",
  "catalog",
  "organization",
];

/** Échappe une valeur serveur avant de l'interpoler entre apostrophes dans du CSS injecté. */
function escapeCssStringValue(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/'/g, "\\'")
    .replace(/[\r\n]/g, "");
}

const FONT_FORMAT_PREFERENCE_LIST: FontFormat[] = [
  "woff2",
  "woff",
  "ttf",
  "otf",
];

export interface FontFaceGroup extends Pick<FontFace, "weight" | "style"> {
  faceList: FontFace[];
}

const FONT_STYLE_ORDER_LIST: FontStyle[] = ["normal", "italic"];

/** Clés i18n des graisses nommées ; les autres (variable fonts) n'ont pas de nom. */
const FONT_WEIGHT_LABEL_KEY_BY_WEIGHT: Partial<Record<number, string>> = {
  100: "fontWeight100",
  200: "fontWeight200",
  300: "fontWeight300",
  400: "fontWeight400",
  500: "fontWeight500",
  600: "fontWeight600",
  700: "fontWeight700",
  800: "fontWeight800",
  900: "fontWeight900",
};

export function getFontWeightLabelKey(weight: number): string | null {
  return FONT_WEIGHT_LABEL_KEY_BY_WEIGHT[weight] ?? null;
}

export function groupFaceListByDescriptor(
  faceList: FontFace[],
): FontFaceGroup[] {
  const groupByDescriptor = new Map<string, FontFaceGroup>();

  faceList.forEach((face) => {
    const descriptor = `${face.weight} ${face.style}`;
    const { weight, style } = face;
    const group = groupByDescriptor.get(descriptor) ?? {
      weight,
      style,
      faceList: [],
    };
    groupByDescriptor.set(descriptor, {
      ...group,
      faceList: [...group.faceList, face],
    });
  });

  return Array.from(groupByDescriptor.values()).sort(
    (first, second) =>
      first.weight - second.weight ||
      FONT_STYLE_ORDER_LIST.indexOf(first.style) -
        FONT_STYLE_ORDER_LIST.indexOf(second.style),
  );
}

function buildFamilyFontFaceCssList(family: FontFamily): string[] {
  return groupFaceListByDescriptor(family.faces).map(
    ({ weight, style, faceList }) => {
      const sourceList = [...faceList]
        .sort(
          (first, second) =>
            FONT_FORMAT_PREFERENCE_LIST.indexOf(first.format) -
            FONT_FORMAT_PREFERENCE_LIST.indexOf(second.format),
        )
        .map(
          (face) =>
            `url('${escapeCssStringValue(face.url)}') format('${CSS_FORMAT_BY_FONT_FORMAT[face.format]}')`,
        );

      return [
        "@font-face{",
        `font-family:'${escapeCssStringValue(family.family_name)}';`,
        `src:${sourceList.join(",")};`,
        `font-weight:${weight};`,
        `font-style:${style};`,
        "font-display:swap;}",
      ].join("");
    },
  );
}

export function buildFontFaceCss(familyList: FontFamily[]): string {
  return familyList
    .filter((family) => family.scope !== "system")
    .flatMap(buildFamilyFontFaceCssList)
    .join("\n");
}

export function findFamilyByUuid(
  familyList: FontFamily[],
  uuid: string | null,
): FontFamily | undefined {
  if (!uuid) return undefined;

  return familyList.find((family) => family.uuid === uuid);
}

export function needsAppsRegeneration(
  familyList: FontFamily[],
  roles: ProjectFontRoles,
): boolean {
  return [roles.title, roles.text].some((uuid) => {
    const family = findFamilyByUuid(familyList, uuid);

    return Boolean(
      family && family.scope !== "system" && !family.bundled_in_apps,
    );
  });
}

function toSiteArchitectureFamily(
  family: FontFamily,
): SiteArchitectureFontFamily {
  return {
    uuid: family.uuid,
    family: family.family_name,
    source: family.scope,
    css_stack: family.css_stack,
    bundled: family.bundled_in_apps,
    faces: family.faces.map(({ weight, style, format, url, size }) => ({
      weight,
      style,
      format,
      url,
      size,
    })),
  };
}

/** Ne garde que les familles référencées par les rôles, sans doublon, dans la forme de `site_architecture.fonts`. */
export function toPreviewFonts(
  roles: ProjectFontRoles,
  familyList: FontFamily[],
): AppearancePreviewState["fonts"] {
  const referencedFamilyList = [...new Set([roles.title, roles.text])]
    .map((uuid) => findFamilyByUuid(familyList, uuid))
    .filter((family): family is FontFamily => family !== undefined);

  return {
    roles,
    families: referencedFamilyList.map(toSiteArchitectureFamily),
  };
}
